import type { Cmd, ServerMsg } from './protocol';

// Same-origin WS, honouring the subpath the game is served from.
// Dev (BASE_URL=/):              ws://host/ws            → vite proxies to Go.
// Prod (BASE_URL=/bharat/):      wss://etoo.in/bharat/ws → Caddy strips
// /bharat and the game nginx proxies /ws to the Go backend.
function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  const base: string = import.meta.env.BASE_URL || '/';
  const path = `${base.endsWith('/') ? base : `${base}/`}ws`;
  return `${proto}//${location.host}${path}`;
}

export type MsgHandler = (m: ServerMsg) => void;
export type ConnStatus = 'connecting' | 'open' | 'closed';
export type StatusHandler = (s: ConnStatus) => void;

export class GameSocket {
  private ws: WebSocket | null = null;
  private handlers = new Set<MsgHandler>();
  private statusHandlers = new Set<StatusHandler>();
  private queue: string[] = [];
  private wantOpen = false;
  private retryTimer: number | null = null;
  private backoff = 1000;
  connected = false;

  onMessage(h: MsgHandler): () => void {
    this.handlers.add(h);
    return () => this.handlers.delete(h);
  }

  onStatus(h: StatusHandler): () => void {
    this.statusHandlers.add(h);
    return () => this.statusHandlers.delete(h);
  }

  private setStatus(s: ConnStatus): void {
    for (const h of this.statusHandlers) h(s);
  }

  connect(): void {
    this.wantOpen = true;
    this.backoff = 1000;
    this.dial();
  }

  retryNow(): void {
    if (!this.wantOpen) this.wantOpen = true;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.backoff = 1000;
    this.dial();
  }

  private dial(): void {
    if (!this.wantOpen) return;
    this.closeSocket();
    this.setStatus('connecting');
    let ws: WebSocket;
    try {
      ws = new WebSocket(wsUrl());
    } catch {
      this.scheduleRetry();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      if (this.ws !== ws) return;
      this.connected = true;
      this.backoff = 1000;
      for (const q of this.queue) ws.send(q);
      this.queue = [];
      this.setStatus('open');
    };
    ws.onmessage = (ev) => {
      try {
        const m = JSON.parse(ev.data as string) as ServerMsg;
        for (const h of this.handlers) h(m);
      } catch {
        /* ignore malformed frames */
      }
    };
    const dead = () => {
      if (this.ws !== ws) return;
      this.ws = null;
      this.connected = false;
      if (!this.wantOpen) return;
      this.setStatus('closed');
      this.scheduleRetry();
    };
    ws.onclose = dead;
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* noop */
      }
    };
  }

  private scheduleRetry(): void {
    if (!this.wantOpen || this.retryTimer !== null) return;
    this.retryTimer = window.setTimeout(() => {
      this.retryTimer = null;
      this.backoff = Math.min(this.backoff * 2, 10000);
      this.dial();
    }, this.backoff);
  }

  private closeSocket(): void {
    try {
      this.ws?.close();
    } catch {
      /* noop */
    }
    this.ws = null;
    this.connected = false;
  }

  disconnect(): void {
    this.wantOpen = false;
    if (this.retryTimer !== null) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    this.closeSocket();
  }

  private sendRaw(obj: unknown): void {
    const s = JSON.stringify(obj);
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(s);
    } else {
      this.queue.push(s);
    }
  }

  listRooms(): void {
    this.sendRaw({ t: 'rooms' });
  }
  create(name: string, size: number, bots: number, difficulty: string, richness: string): void {
    this.sendRaw({ t: 'create', name, size, bots, difficulty, richness });
  }
  join(room: string, name: string): void {
    this.sendRaw({ t: 'join', room, name });
  }
  addBot(): void {
    this.sendRaw({ t: 'add_bot' });
  }
  setDiff(difficulty: string): void {
    this.sendRaw({ t: 'set_diff', difficulty });
  }
  setRich(richness: string): void {
    this.sendRaw({ t: 'set_rich', richness });
  }
  start(): void {
    this.sendRaw({ t: 'start' });
  }
  leave(): void {
    this.sendRaw({ t: 'leave' });
  }
  cmd(c: Cmd): void {
    this.sendRaw({ t: 'cmd', cmd: c });
  }
}
